import { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { buildQuickMetierPayload } from '../../utils/taxonomyQuickCreate';

const BOOL_FIELDS = [
  { key: 'moduleProduits', label: 'Module produits' },
  { key: 'modulePrestations', label: 'Module prestations' },
  { key: 'moduleCaisse', label: 'Module caisse' },
  { key: 'moduleCreneaux', label: 'Module créneaux' },
  { key: 'priseRDV', label: 'Prise de RDV' },
  { key: 'priseCreneaux', label: 'Prise de créneaux' },
  { key: 'reservationTable', label: 'Réservation table' },
  { key: 'livraison', label: 'Livraison' },
  { key: 'clickAndCollect', label: 'Click & Collect' },
  { key: 'paiementEnLigne', label: 'Paiement en ligne' },
  { key: 'paiementSurPlace', label: 'Paiement sur place' },
];

const EMPTY = {
  id: '',
  familyId: '',
  label: '',
  icon: '',
  description: '',
  badge: '',
  profileKey: '',
  catalogOrientation: 'PRODUITS',
  sortOrder: 0,
  moduleProduits: false,
  modulePrestations: false,
  moduleCaisse: false,
  moduleCreneaux: false,
  priseRDV: false,
  priseCreneaux: false,
  reservationTable: false,
  livraison: false,
  clickAndCollect: false,
  paiementEnLigne: true,
  paiementSurPlace: true,
  pasGrilleMinutes: 15,
  dureesAutoriseesMinutes: '60,90,120',
  devise: 'EUR',
};

/**
 * Modale création / édition métier taxonomie.
 * variant="quick" : picker commerçant (libellé + famille, defaults auto).
 * variant="full" (défaut) : page Familles & métiers.
 */
export default function MetierFormModal({
  open,
  onClose,
  onSubmit,
  initial = null,
  families = [],
  defaultFamilyId = '',
  submitting = false,
  variant = 'full',
  existingMetierIds = [],
}) {
  const isQuick = variant === 'quick';
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(EMPTY);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        id: initial.id || '',
        familyId: initial.familyId || initial.family || '',
        label: initial.label || '',
        icon: initial.icon || '',
        description: initial.description || '',
        badge: initial.badge || '',
        profileKey: initial.profileKey || '',
        catalogOrientation: initial.catalogOrientation || 'PRODUITS',
        sortOrder: initial.sortOrder ?? 0,
        moduleProduits: Boolean(initial.moduleProduits),
        modulePrestations: Boolean(initial.modulePrestations),
        moduleCaisse: Boolean(initial.moduleCaisse),
        moduleCreneaux: Boolean(initial.moduleCreneaux),
        priseRDV: Boolean(initial.priseRDV),
        priseCreneaux: Boolean(initial.priseCreneaux),
        reservationTable: Boolean(initial.reservationTable),
        livraison: Boolean(initial.livraison),
        clickAndCollect: Boolean(initial.clickAndCollect),
        paiementEnLigne: initial.paiementEnLigne !== false,
        paiementSurPlace: initial.paiementSurPlace !== false,
        pasGrilleMinutes: initial.pasGrilleMinutes ?? 15,
        dureesAutoriseesMinutes: Array.isArray(initial.dureesAutoriseesMinutes)
          ? initial.dureesAutoriseesMinutes.join(',')
          : '60,90,120',
        devise: initial.devise || 'EUR',
      });
    } else {
      setForm({ ...EMPTY, familyId: defaultFamilyId || '' });
    }
    setFieldError('');
  }, [open, initial, defaultFamilyId]);

  const setBool = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const buildPayload = () => {
    const durees = String(form.dureesAutoriseesMinutes || '')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    return {
      label: form.label.trim(),
      familyId: form.familyId,
      icon: form.icon.trim() || null,
      description: form.description.trim() || null,
      badge: form.badge.trim() || null,
      profileKey: form.profileKey.trim() || form.id.trim().toUpperCase(),
      catalogOrientation: form.catalogOrientation,
      sortOrder: Number(form.sortOrder) || 0,
      moduleProduits: form.moduleProduits,
      modulePrestations: form.modulePrestations,
      moduleCaisse: form.moduleCaisse,
      moduleCreneaux: form.moduleCreneaux,
      priseRDV: form.priseRDV,
      priseCreneaux: form.priseCreneaux,
      reservationTable: form.reservationTable,
      livraison: form.livraison,
      clickAndCollect: form.clickAndCollect,
      paiementEnLigne: form.paiementEnLigne,
      paiementSurPlace: form.paiementSurPlace,
      pasGrilleMinutes: Number(form.pasGrilleMinutes) || 15,
      dureesAutoriseesMinutes: durees.length ? durees : [60, 90, 120],
      devise: form.devise.trim() || 'EUR',
    };
  };

  const handleSubmit = async (e) => {
    // Empêche le submit du <form> commerçant parent (modales imbriquées).
    e.preventDefault();
    e.stopPropagation();
    if (submitting) return;
    const label = form.label.trim();
    if (!label) {
      setFieldError('Le libellé est obligatoire.');
      return;
    }
    if (!form.familyId) {
      setFieldError('Sélectionnez une famille.');
      return;
    }

    if (isQuick && !isEdit) {
      try {
        const family = families.find((f) => f.id === form.familyId);
        const payload = buildQuickMetierPayload(label, form.familyId, {
          existingMetierIds,
          familyIcon: family?.icon || null,
        });
        await onSubmit(payload);
      } catch (err) {
        setFieldError(err?.message || 'Création impossible.');
      }
      return;
    }

    if (!isEdit) {
      const id = form.id.trim().toUpperCase();
      if (!id || !/^[A-Z][A-Z0-9_]*$/.test(id)) {
        setFieldError('Code métier : MAJUSCULES, chiffres et _ (ex. TRAITEUR).');
        return;
      }
      await onSubmit({ id, ...buildPayload() });
      return;
    }
    await onSubmit(buildPayload());
  };

  const title = isQuick
    ? 'Créer un métier'
    : isEdit
      ? 'Modifier le métier'
      : 'Ajouter un métier';

  const activeFamilies = (families || []).filter(
    (f) => f.actif !== false && !f.archivedAt,
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
      maxWidth={isQuick ? 'max-w-lg' : 'max-w-2xl'}
    >
      <form
        onSubmit={handleSubmit}
        className={`px-6 py-4 space-y-4 ${isQuick ? '' : 'max-h-[70vh] overflow-y-auto'}`}
      >
        {fieldError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {fieldError}
          </p>
        )}

        {isQuick && !isEdit ? (
          <>
            <p className="text-xs text-gray-500">
              Le code et les modules sont définis automatiquement selon la famille.
              Affinez ensuite dans Familles &amp; métiers si besoin.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Libellé du métier *
              </label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex. Traiteur événementiel"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Famille *</label>
              <select
                value={form.familyId}
                onChange={(e) => setForm({ ...form, familyId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              >
                <option value="">— Choisir —</option>
                {activeFamilies.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.icon ? `${f.icon} ` : ''}
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              Orientation catalogue et profileKey servent à l&apos;identité soft (C10) — ils ne
              contrôlent pas les modules activés ci-dessous.
            </p>
            {!isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code (id) *</label>
                <input
                  type="text"
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value.toUpperCase() })}
                  placeholder="TRAITEUR"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  disabled={submitting}
                />
              </div>
            )}
            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <p className="text-sm text-gray-600 font-mono">{form.id}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Famille *</label>
              <select
                value={form.familyId}
                onChange={(e) => setForm({ ...form, familyId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              >
                <option value="">— Choisir —</option>
                {families.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.icon ? `${f.icon} ` : ''}
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
                <input
                  type="text"
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                <input
                  type="text"
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">profileKey</label>
                <input
                  type="text"
                  value={form.profileKey}
                  onChange={(e) => setForm({ ...form, profileKey: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orientation catalogue
                </label>
                <select
                  value={form.catalogOrientation}
                  onChange={(e) => setForm({ ...form, catalogOrientation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                >
                  <option value="PRODUITS">PRODUITS</option>
                  <option value="PRESTATIONS">PRESTATIONS</option>
                  <option value="MIXTE">MIXTE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>
            <fieldset className="border border-gray-200 rounded-lg p-3">
              <legend className="text-sm font-medium text-gray-700 px-1">Modules & modes</legend>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {BOOL_FIELDS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(form[key])}
                      onChange={(e) => setBool(key, e.target.checked)}
                      disabled={submitting}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pas grille (min)
                </label>
                <input
                  type="number"
                  value={form.pasGrilleMinutes}
                  onChange={(e) => setForm({ ...form, pasGrilleMinutes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durées (min, csv)
                </label>
                <input
                  type="text"
                  value={form.dureesAutoriseesMinutes}
                  onChange={(e) => setForm({ ...form, dureesAutoriseesMinutes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
                <input
                  type="text"
                  value={form.devise}
                  onChange={(e) => setForm({ ...form, devise: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type={isQuick && !isEdit ? 'button' : 'submit'}
            disabled={submitting}
            onClick={
              isQuick && !isEdit
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(e);
                  }
                : undefined
            }
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
          >
            {submitting
              ? 'Création…'
              : isQuick && !isEdit
                ? 'Créer et sélectionner'
                : isEdit
                  ? 'Enregistrer'
                  : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
